package dev.lingvo.site.components.editor

import dev.lingvo.shared.PluralCategory
import dev.lingvo.site.ds.LPresencePhase
import dev.lingvo.site.ds.LPresenceState

internal enum class EditorViewportClass { COMPACT, WORKBENCH }

internal enum class EditorMeasuredLayoutDisposition {
    NOT_APPLICABLE,
    MEASURING,
    WORKBENCH,
    COMPACT_FALLBACK,
}

internal enum class EditorPrimaryView { QUEUE, EDITOR }
internal enum class EditorQueueMode { LIST, FILTERS }
internal enum class EditorUtilitySurface { NONE, ASSIST, DISCUSS, ACTIVITY, DETAILS }
internal enum class EditorModalSurface { NONE, CLEAR_CONFIRMATION, SHORTCUTS, BULK_CONFIRMATION }

internal enum class EditorUtilityPresencePhase(val attributeValue: String) {
    CLOSED("closed"),
    PENDING_ENTER("pending-enter"),
    ENTERING("entering"),
    PRESENT("present"),
    EXITING("exiting"),
}

internal enum class EditorUtilityMotionPolicy { STANDARD, INSTANT }

internal enum class EditorUtilityActionOrigin { POINTER, KEYBOARD }

/**
 * The editor's measured-layout wrapper around the shared generation-safe [LPresenceState].
 *
 * [pendingEnter] is the one phase shared presence deliberately does not need: Assist must mount its
 * unanimated measurement probe first, then expose a starting style only after that probe chooses a
 * sidecar or sheet. Every other phase and stale-completion rule delegates to [LPresenceState].
 */
internal data class EditorUtilityPresenceState(
    val presence: LPresenceState = LPresenceState.initial(open = false),
    val pendingEnter: Boolean = false,
    val motionPolicy: EditorUtilityMotionPolicy = EditorUtilityMotionPolicy.STANDARD,
) {
    val phase: EditorUtilityPresencePhase
        get() = when {
            pendingEnter -> EditorUtilityPresencePhase.PENDING_ENTER
            presence.phase == LPresencePhase.HIDDEN -> EditorUtilityPresencePhase.CLOSED
            presence.phase == LPresencePhase.ENTERING -> EditorUtilityPresencePhase.ENTERING
            presence.phase == LPresencePhase.PRESENT -> EditorUtilityPresencePhase.PRESENT
            else -> EditorUtilityPresencePhase.EXITING
        }

    val generation: Long
        get() = presence.generation

    fun requestOpen(
        policy: EditorUtilityMotionPolicy,
        alreadyMounted: Boolean,
    ): EditorUtilityPresenceState = when {
        policy == EditorUtilityMotionPolicy.INSTANT -> copy(
            presence = LPresenceState(
                rendered = true,
                phase = LPresencePhase.PRESENT,
                generation = generation + 1,
            ),
            pendingEnter = false,
            motionPolicy = policy,
        )

        alreadyMounted && phase == EditorUtilityPresencePhase.EXITING -> copy(
            presence = presence.request(open = true),
            pendingEnter = false,
            motionPolicy = policy,
        )

        else -> copy(
            presence = LPresenceState(
                rendered = true,
                phase = LPresencePhase.HIDDEN,
                generation = generation + 1,
            ),
            pendingEnter = true,
            motionPolicy = policy,
        )
    }

    fun advanceEnter(
        requestGeneration: Long,
        settleImmediately: Boolean,
    ): EditorUtilityPresenceState = when {
        requestGeneration != generation -> this
        phase == EditorUtilityPresencePhase.PENDING_ENTER -> copy(
            presence = LPresenceState(
                rendered = true,
                phase = if (settleImmediately) LPresencePhase.PRESENT else LPresencePhase.ENTERING,
                generation = generation,
            ),
            pendingEnter = false,
        )

        phase == EditorUtilityPresencePhase.ENTERING -> copy(
            presence = presence.finishEnter(requestGeneration),
        )

        else -> this
    }

    fun requestExit(settleImmediately: Boolean): EditorUtilityPresenceState = if (settleImmediately) {
        copy(
            presence = LPresenceState(
                rendered = false,
                phase = LPresencePhase.HIDDEN,
                generation = generation + 1,
            ),
            pendingEnter = false,
        )
    } else {
        copy(
            presence = if (phase == EditorUtilityPresencePhase.PENDING_ENTER) {
                LPresenceState(
                    rendered = true,
                    phase = LPresencePhase.EXITING,
                    generation = generation + 1,
                )
            } else {
                presence.request(open = false)
            },
            // **An animated exit carries the policy that animates it.**
            //
            // `motionPolicy` was whatever the *open* recorded, and the two modalities cross: a
            // keyboard open records INSTANT, while only a keyboard *close* settles on the spot. So
            // opening with Alt+H and closing with the mouse produced EXITING under INSTANT — a
            // combination whose completion path returned early, leaving the panel mounted off
            // screen with its scrim over the editor for good.
            //
            // Reaching this branch at all means the caller asked for an animated exit. Saying so
            // in the state makes the two agree, rather than leaving the effect to reconcile them.
            motionPolicy = EditorUtilityMotionPolicy.STANDARD,
            pendingEnter = false,
        )
    }

    fun finishExit(requestGeneration: Long): EditorUtilityPresenceState = if (
        requestGeneration == generation && phase == EditorUtilityPresencePhase.EXITING
    ) {
        copy(
            presence = presence.finishExit(requestGeneration),
            pendingEnter = false,
        )
    } else {
        this
    }

    companion object {
        fun closed(): EditorUtilityPresenceState = EditorUtilityPresenceState()

        fun legacyPresent(): EditorUtilityPresenceState = EditorUtilityPresenceState(
            presence = LPresenceState.initial(open = true),
            motionPolicy = EditorUtilityMotionPolicy.INSTANT,
        )
    }
}

internal sealed interface EditorFocusRef {
    data class Captured(val token: Long) : EditorFocusRef
    data class ElementId(val value: String) : EditorFocusRef
    data class QueueRow(val keyId: String) : EditorFocusRef
    data class Target(
        val keyId: String,
        val pluralCategory: PluralCategory?,
        val locale: String? = null,
    ) : EditorFocusRef

    /**
     * A durable caret destination across measured-workbench remounts.
     *
     * Unlike [Captured], this stores no DOM node. Each focus effect resolves [target] against the
     * current composition, then reapplies the immutable selection without consuming it. The same
     * snapshot can therefore settle focus after measurement and return it again after close.
     */
    data class TargetSnapshot(
        val target: Target,
        val selectionStart: Int,
        val selectionEnd: Int,
        val selectionDirection: String? = null,
    ) : EditorFocusRef

    data object Search : EditorFocusRef
}

internal data class EditorDeferredFocus(
    val target: EditorFocusRef,
    val fallback: EditorFocusRef?,
)

internal enum class EditorViewEffectOutcome { COMPLETED, FAILED, SUPERSEDED }

internal data class EditorViewEffectResult(
    val effectId: Long,
    val outcome: EditorViewEffectOutcome,
    val detail: String? = null,
)

internal sealed interface EditorViewEffect {
    val id: Long

    data class Focus(
        override val id: Long,
        val target: EditorFocusRef,
        val fallback: EditorFocusRef?,
    ) : EditorViewEffect

    data class RestoreQueueScroll(
        override val id: Long,
        val topPx: Double,
    ) : EditorViewEffect
}

internal data class EditorTransition(
    val state: EditorWorkspaceState,
    val effects: List<EditorViewEffect> = emptyList(),
)

internal data class EditorWorkspaceState(
    val viewportWidth: Int = 0,
    val viewport: EditorViewportClass = EditorViewportClass.COMPACT,
    val measuredLayoutDisposition: EditorMeasuredLayoutDisposition =
        EditorMeasuredLayoutDisposition.NOT_APPLICABLE,
    val layoutMeasurementId: Long = 0,
    val primaryView: EditorPrimaryView = EditorPrimaryView.QUEUE,
    val queueMode: EditorQueueMode = EditorQueueMode.LIST,
    val utility: EditorUtilitySurface = EditorUtilitySurface.NONE,
    val utilityReturnFocus: EditorFocusRef? = null,
    val utilityPresence: EditorUtilityPresenceState = EditorUtilityPresenceState.closed(),
    val utilityOpenFocus: EditorDeferredFocus? = null,
    val utilityCloseFocus: EditorDeferredFocus? = null,
    val filtersReturnFocus: EditorFocusRef? = null,
    val overflowReturnFocus: EditorFocusRef? = null,
    val modalReturnFocus: EditorFocusRef? = null,
    val overflowOpen: Boolean = false,
    val modal: EditorModalSurface = EditorModalSurface.NONE,
    val queueScrollTopPx: Double = 0.0,
    val queueQueryRevision: Long = 0,
    val pendingQueueScrollResetRevision: Long? = null,
    val focusAfterQueueRows: EditorDeferredFocus? = null,
    val nextEffectId: Long = 1,
    val measuredScrollbarGutterPx: Double? = null,
    val editorSidePaddingPx: Int = 32,
    val focusAfterMeasurement: EditorDeferredFocus? = null,
) {
    init {
        validate()
    }

    internal fun validate() {
        require(layoutMeasurementId >= 0)
        require(nextEffectId > 0)
        require(editorSidePaddingPx in EDITOR_MIN_SIDE_PADDING_PX..EDITOR_PREFERRED_SIDE_PADDING_PX)
        require(queueScrollTopPx >= 0.0)
        require(queueQueryRevision >= 0)
        require(measuredScrollbarGutterPx == null || measuredScrollbarGutterPx >= 0.0)
        require(
            viewport == if (viewportWidth < EDITOR_WORKBENCH_THRESHOLD_PX) {
                EditorViewportClass.COMPACT
            } else {
                EditorViewportClass.WORKBENCH
            },
        )

        when (measuredLayoutDisposition) {
            EditorMeasuredLayoutDisposition.NOT_APPLICABLE -> {
                require(viewport == EditorViewportClass.COMPACT)
                require(measuredScrollbarGutterPx == null)
            }

            EditorMeasuredLayoutDisposition.MEASURING -> {
                require(viewport == EditorViewportClass.WORKBENCH)
                if (editorSidePaddingPx == EDITOR_PREFERRED_SIDE_PADDING_PX) {
                    require(measuredScrollbarGutterPx == null)
                }
            }

            EditorMeasuredLayoutDisposition.WORKBENCH,
            EditorMeasuredLayoutDisposition.COMPACT_FALLBACK,
            -> {
                require(viewport == EditorViewportClass.WORKBENCH)
                require(measuredScrollbarGutterPx != null)
            }
        }

        require(
            pendingQueueScrollResetRevision == null ||
                pendingQueueScrollResetRevision == queueQueryRevision,
        )
        if (focusAfterQueueRows != null) {
            require(pendingQueueScrollResetRevision != null)
            require(queueMode == EditorQueueMode.LIST)
            require(mountsQueueList())
        }
    }
}

internal const val EDITOR_WORKBENCH_THRESHOLD_PX = 960
internal const val EDITOR_COMPACT_QUEUE_DENSITY_THRESHOLD_PX = 600
internal const val EDITOR_MIN_WRITING_WIDTH_PX = 560
internal const val EDITOR_PREFERRED_SIDE_PADDING_PX = 32
internal const val EDITOR_MIN_SIDE_PADDING_PX = 16

internal enum class EditorRenderedLayout { MEASURING, WORKBENCH, COMPACT }
internal enum class EditorUtilityShell { MEASURING, SIDECAR, SHEET }

internal fun EditorWorkspaceState.resolvedUtilityPresence(): EditorUtilityPresenceState =
    if (
        utility != EditorUtilitySurface.NONE &&
        utilityPresence.phase == EditorUtilityPresencePhase.CLOSED &&
        utilityPresence.generation == 0L
    ) {
        EditorUtilityPresenceState.legacyPresent()
    } else {
        utilityPresence
    }

internal fun EditorWorkspaceState.utilityPresencePhase(): EditorUtilityPresencePhase =
    resolvedUtilityPresence().phase

internal val EditorWorkspaceState.utilityPresenceGeneration: Long
    get() = resolvedUtilityPresence().generation

internal fun EditorWorkspaceState.renderedLayout(): EditorRenderedLayout = when (
    measuredLayoutDisposition
) {
    EditorMeasuredLayoutDisposition.MEASURING -> EditorRenderedLayout.MEASURING
    EditorMeasuredLayoutDisposition.WORKBENCH -> EditorRenderedLayout.WORKBENCH
    EditorMeasuredLayoutDisposition.NOT_APPLICABLE,
    EditorMeasuredLayoutDisposition.COMPACT_FALLBACK,
    -> EditorRenderedLayout.COMPACT
}

internal fun EditorWorkspaceState.layoutReason(): String = when (measuredLayoutDisposition) {
    EditorMeasuredLayoutDisposition.NOT_APPLICABLE -> "width"
    EditorMeasuredLayoutDisposition.MEASURING -> "measuring-writing-width"
    EditorMeasuredLayoutDisposition.WORKBENCH -> "measured-workbench"
    EditorMeasuredLayoutDisposition.COMPACT_FALLBACK -> "measured-writing-fallback"
}

internal fun EditorWorkspaceState.primaryViewHook(): String = when (renderedLayout()) {
    EditorRenderedLayout.MEASURING -> "measuring"
    EditorRenderedLayout.WORKBENCH -> "editor"
    EditorRenderedLayout.COMPACT -> primaryView.name.lowercase()
}

internal fun EditorWorkspaceState.mountsQueueList(): Boolean = when (renderedLayout()) {
    EditorRenderedLayout.MEASURING,
    EditorRenderedLayout.WORKBENCH,
    -> queueMode == EditorQueueMode.LIST &&
        (utility == EditorUtilitySurface.NONE || viewportWidth >= EDITOR_TRIO_THRESHOLD_PX)

    EditorRenderedLayout.COMPACT -> primaryView == EditorPrimaryView.QUEUE &&
        utility == EditorUtilitySurface.NONE &&
        queueMode == EditorQueueMode.LIST
}

internal fun editorQueueSelectionFocusTarget(
    workspace: EditorWorkspaceState,
    keyId: String,
): EditorFocusRef.QueueRow? = keyId
    .takeIf { workspace.mountsQueueList() }
    ?.let(EditorFocusRef::QueueRow)

internal const val EDITOR_HEADER_ASSIST_TRIGGER_ID = "lingvo-editor-assist-trigger"
internal const val EDITOR_INLINE_ASSIST_TRIGGER_ID = "lingvo-editor-inline-assist-trigger"
internal const val EDITOR_DETAILS_TRIGGER_ID = "lingvo-editor-details-trigger"
internal const val EDITOR_FILTERS_TRIGGER_ID = "lingvo-editor-filters-trigger"
internal const val EDITOR_OVERFLOW_TRIGGER_ID = "lingvo-editor-overflow-trigger"
internal const val EDITOR_UTILITY_CLOSE_ID = "lingvo-editor-utility-close"
internal const val EDITOR_CONFLICT_TARGET_ID = "lingvo-editor-conflict-target"

/**
 * The action bar's QA blocker band, named so the Approve button can point at it.
 *
 * The band is the only statement of why Approve is unavailable, and it was connected to that button
 * by nothing but proximity — invisible to anyone reading the bar through the accessibility tree.
 */
internal const val EDITOR_QA_BLOCKER_ID = "lingvo-editor-qa-blocker"
internal const val EDITOR_CLEAR_MODAL_INITIAL_FOCUS_ID = "lingvo-editor-clear-cancel"
internal const val EDITOR_SHORTCUTS_MODAL_INITIAL_FOCUS_ID = "lingvo-editor-shortcuts-close"
internal const val EDITOR_BULK_MODAL_INITIAL_FOCUS_ID = "lingvo-editor-bulk-cancel"
internal const val EDITOR_SUMMARY_TOGGLE_ID = "lingvo-editor-summary-toggle"
internal const val EDITOR_MODAL_SURFACE_ID = "lingvo-editor-modal-surface"
internal const val EDITOR_UTILITY_SURFACE_ID = "lingvo-editor-utility-surface"
internal const val EDITOR_SHEET_SURFACE_ID = EDITOR_UTILITY_SURFACE_ID
internal const val EDITOR_UTILITY_PANEL_ID = "lingvo-editor-utility-panel"
internal const val EDITOR_UTILITY_TAB_ASSIST_ID = "lingvo-editor-utility-tab-assist"
internal const val EDITOR_UTILITY_TAB_DISCUSS_ID = "lingvo-editor-utility-tab-discuss"
internal const val EDITOR_UTILITY_TAB_ACTIVITY_ID = "lingvo-editor-utility-tab-activity"
internal const val EDITOR_UTILITY_DETAILS_LABEL_ID = "lingvo-editor-utility-details-label"
